/**
 * WelcomeScreenModal — the one-time "Welcome to Life4Earth" first-login
 * screen, Variant A "the handshake" (Skip-approved 2026-07-08 from
 * `welcome-screen-redesign-mockup.html`): the L4E 2026 mark and the
 * inviting org's avatar joined side by side — the two institutions the
 * newcomer trusts, saying they both expected them.
 *
 * NOT a wizard (§7.9): one screen, no steps, no gate. Three entry paths:
 *   1. AUTO — first authenticated load for a profiled user without the
 *      welcome_screen_seen flag (App.tsx host records the flag on close).
 *   2. "Read me first" Getting-started row (ActionCenterInbox host records
 *      the ack flag on close) — §7.9 pointer/content discipline.
 *   3. Admin PREVIEW — Admin Console → Onboarding → Copy tab (no flags).
 *
 * Copy resolves through the onboarding_copy override layer (DB → static
 * i18n → EN); the BODY supports light markdown (bold, bullets, paragraphs)
 * via the shared EulaModal renderer, sanitized through sanitizeRichText
 * per the CLAUDE.md dangerouslySetInnerHTML policy. The org name/avatar
 * are runtime data resolved self-contained here (lineage → community
 * entity → effective logo), so every host gets them for free; users with
 * no inviting org fall back to a plain "Welcome to Life4Earth" header.
 */

import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  useOnboardingCopy,
  resolveCopyOverride,
} from '../../api/onboardingCopy';
import { useApiOnboarding } from '../../api/useApiOnboarding';
import { useApiCommunityData } from '../../api/useApiCommunityData';
import { resolveEffectiveLogoUrl } from '../../api/communityMetadata';
import { mediaSrc } from '../../api/mediaUrl';
import { getCurrentUserGuid } from '../../api/client';
import { renderMarkdown } from '../EulaModal/EulaModal';
import { sanitizeRichText } from '../../utils/sanitizeHtml';
import i18n from '../../i18n/config';
import styles from './WelcomeScreenModal.module.css';

export interface WelcomeScreenModalProps {
  open: boolean;
  /** Close (hosts record the seen/ack flags — not here). */
  onClose: () => void;
  /** Route to the Action Center on the primary CTA (admin preview: false). */
  navigateOnCta?: boolean;
}

function communityInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase();
}

const WelcomeScreenModal: React.FC<WelcomeScreenModalProps> = ({
  open,
  onClose,
  navigateOnCta = true,
}) => {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();
  const { copyMap } = useOnboardingCopy(open);
  const lang = i18n.language || 'en';

  // Inviting org — resolved self-contained (all hooks cached app-wide).
  const currentUserGuid = getCurrentUserGuid();
  const { invitedCommunityGuid } = useApiOnboarding(open ? currentUserGuid : null);
  const { communityEntities, communityRelationships } = useApiCommunityData();

  const org = useMemo(() => {
    if (!open || !invitedCommunityGuid) return null;
    const guidUpper = String(invitedCommunityGuid).toUpperCase();
    const entity = (communityEntities || []).find(
      (e: any) => String(e.entity_guid || '').toUpperCase() === guidUpper,
    );
    if (!entity) return null;
    const logoRaw = resolveEffectiveLogoUrl(
      invitedCommunityGuid,
      communityEntities || [],
      communityRelationships || [],
    );
    return {
      name: String((entity as any).name || ''),
      logoUrl: logoRaw ? mediaSrc(logoRaw) : null,
    };
  }, [open, invitedCommunityGuid, communityEntities, communityRelationships]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const copy = (shortKey: string, vars?: Record<string, any>): string =>
    resolveCopyOverride(copyMap, lang, `pages:${shortKey}`, vars) ??
    t(shortKey, vars as never);

  const bodyHtml = sanitizeRichText(renderMarkdown(copy('welcomeScreen.body')));
  // A "-" override hides a bullet row (clearing an override restores the
  // shipped default, so a dedicated hide sentinel is needed).
  const bullets = [
    copy('welcomeScreen.bullet1'),
    copy('welcomeScreen.bullet2'),
    copy('welcomeScreen.bullet3'),
  ].filter((b) => b.trim().length > 0 && b.trim() !== '-');

  return (
    <div className={styles.backdrop} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={copy('welcomeScreen.title')}
      >
        <button
          type="button"
          className={styles.close}
          aria-label={t('welcomeScreen.close')}
          onClick={onClose}
        >
          ×
        </button>

        {/* The handshake — L4E 2026 mark + inviting org, joined. */}
        <div className={styles.handshake} aria-hidden="true">
          <img
            src="/l4e-logo-2026-192.png"
            alt=""
            className={styles.mark}
          />
          {org && (
            <>
              <span className={styles.plus}>+</span>
              {org.logoUrl ? (
                <img src={org.logoUrl} alt="" className={styles.orgChipImg} />
              ) : (
                <span className={styles.orgChip}>{communityInitials(org.name)}</span>
              )}
            </>
          )}
        </div>

        <p className={styles.eyebrow}>
          {org
            ? copy('welcomeScreen.invitedEyebrow', { org: org.name })
            : copy('welcomeScreen.welcomeEyebrow')}
        </p>
        <p className={styles.wordmark}>
          <em>Life</em>4Earth
        </p>

        {/* Light-markdown body (bold, bullets, paragraphs) — admin-editable. */}
        <div
          className={styles.body}
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />

        {bullets.length > 0 && (
          <div className={styles.bullets}>
            {bullets.map((b, i) => (
              <div key={i} className={styles.bullet}>
                <span className={styles.bulletDot} aria-hidden="true" />
                <span>{b}</span>
              </div>
            ))}
          </div>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={onClose}>
            {t('welcomeScreen.later')}
          </button>
          <button
            type="button"
            className={styles.primary}
            onClick={() => {
              onClose();
              if (navigateOnCta) navigate('/actions');
            }}
          >
            {t('welcomeScreen.cta')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreenModal;
